import { useState, useEffect, useCallback } from 'react';
import { Network } from '@capacitor/network';
import { useCapacitor } from './useCapacitor';
import { toast } from 'sonner';

interface NetworkStatus {
  connected: boolean;
  connectionType: 'wifi' | 'cellular' | 'none' | 'unknown';
}

export const useNetworkStatus = () => {
  const { isNative } = useCapacitor();
  const [status, setStatus] = useState<NetworkStatus>({
    connected: true,
    connectionType: 'unknown'
  });
  const [wasOffline, setWasOffline] = useState(false);

  const checkStatus = useCallback(async () => {
    if (!isNative) {
      // Web fallback
      setStatus({
        connected: navigator.onLine,
        connectionType: 'unknown'
      });
      return;
    }

    try {
      const networkStatus = await Network.getStatus();
      setStatus({
        connected: networkStatus.connected,
        connectionType: networkStatus.connectionType
      });
    } catch (error) {
      console.error('Network status check error:', error);
    }
  }, [isNative]);

  useEffect(() => {
    checkStatus();

    if (isNative) {
      // Native network listener
      let listenerHandle: any;
      
      Network.addListener('networkStatusChange', (networkStatus) => {
        const newConnected = networkStatus.connected;
        
        setStatus({
          connected: newConnected,
          connectionType: networkStatus.connectionType
        });

        // Show notifications on status change
        if (!newConnected && status.connected) {
          setWasOffline(true);
          toast.error('No internet connection', {
            description: 'Some features may be unavailable'
          });
        } else if (newConnected && wasOffline) {
          setWasOffline(false);
          toast.success('Back online', {
            description: 'Internet connection restored'
          });
        }
      }).then(handle => {
        listenerHandle = handle;
      });

      return () => {
        if (listenerHandle) {
          listenerHandle.remove();
        }
      };
    } else {
      // Web fallback listeners
      const handleOnline = () => {
        setStatus(prev => ({ ...prev, connected: true }));
        if (wasOffline) {
          setWasOffline(false);
          toast.success('Back online');
        }
      };

      const handleOffline = () => {
        setStatus(prev => ({ ...prev, connected: false }));
        setWasOffline(true);
        toast.error('No internet connection');
      };

      window.addEventListener('online', handleOnline);
      window.addEventListener('offline', handleOffline);

      return () => {
        window.removeEventListener('online', handleOnline);
        window.removeEventListener('offline', handleOffline);
      };
    }
  }, [isNative, checkStatus, status.connected, wasOffline]);

  return {
    isOnline: status.connected,
    isOffline: !status.connected,
    connectionType: status.connectionType,
    checkStatus
  };
};
